import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { fetchChallengesDetails, updateChallenge } from '../../services/ChallengesService';
import ChallengesTable from './ChallengesTable';
import FilterChallengeSidebar from './FilterChallengeSidebar';

function ChallengeLibrary() {
  const { isAuthenticated, user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [challengesDetails, setChallengesDetails] = useState([]);
  const [filteredChallenges, setFilteredChallenges] = useState([]);

  const params = new URLSearchParams(location.search);
  const [filters, setFilters] = useState({
    level: params.getAll('level'),
    type: params.getAll('type'),
    category: params.getAll('category'),
  });
  const [currentPage, setCurrentPage] = useState(Number(params.get('page')) || 1);
  const [sortOption, setSortOption] = useState(params.get('sort') || '');
  const [searchTerm, setSearchTerm] = useState(params.get('search') || '');

  const resultsPerPage = 10;

  // Fetch data
  useEffect(() => {
    if (isAuthenticated) {
      const getChallengesDetails = async () => {
        try {
          const details = await fetchChallengesDetails();
          setChallengesDetails(details);
          setLoading(false);
        } catch (error) {
          console.error(error);
        }
      };
      getChallengesDetails();
    }
  }, [isAuthenticated]);

  // Apply filters and sort
  useEffect(() => {
    let results = challengesDetails.filter(challenge => {
      const match = (field, values) => values.length === 0 || values.includes(challenge[field]);
      return match('level', filters.level) && match('type', filters.type) && match('category', filters.category);
    });

    if (searchTerm) {
      results = results.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()));
    }

    if (sortOption) {
      results.sort((a, b) => {
        const valA = (a[sortOption] || '').toString().toLowerCase();
        const valB = (b[sortOption] || '').toString().toLowerCase();
        return valA.localeCompare(valB);
      });
    }

    setFilteredChallenges(results);
  }, [filters, challengesDetails, searchTerm, sortOption]);

  // Sync query params
  useEffect(() => {
    const query = new URLSearchParams();
    filters.level.forEach(val => query.append('level', val));
    filters.type.forEach(val => query.append('type', val));
    filters.category.forEach(val => query.append('category', val));
    if (searchTerm) query.set('search', searchTerm);
    if (sortOption) query.set('sort', sortOption);
    if (currentPage) query.set('page', currentPage);
    navigate({ search: query.toString() }, { replace: true });
  }, [filters, sortOption, searchTerm, currentPage]);

  const handlePageChange = (page) => setCurrentPage(page);

  const numberOfResults = filteredChallenges.length;
  const currentResults = filteredChallenges.slice(
    (currentPage - 1) * resultsPerPage,
    currentPage * resultsPerPage
  );

  const onUpdateChallenge = async (id, updatedFields) => {
    try {
      const updated = await updateChallenge(id, updatedFields);
      setChallengesDetails(prev =>
        prev.map(ch => ch.id === id ? { ...ch, ...updatedFields } : ch)
      );
    } catch (e) {
      console.error('Error updating:', e);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <div className="main-container">
      <FilterChallengeSidebar filters={filters} setFilters={setFilters} />
      <div className="table-container">
        {loading ? (
          <p>Loading Challenges Details...</p>
        ) : (
          <ChallengesTable
            challenges={currentResults}
            currentPage={currentPage}
            resultsPerPage={resultsPerPage}
            onPageChange={handlePageChange}
            totalResults={numberOfResults}
            setSortOption={setSortOption}
            setSearchTerm={setSearchTerm}
            sortOption={sortOption}
            searchTerm={searchTerm}
            onUpdateChallenge={onUpdateChallenge}
            filters={filters}
          />
        )}
      </div>
    </div>
  );
}

export default ChallengeLibrary;
